import { useState, useEffect, useMemo } from 'react';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { formatPercentNumber } from '@helpers/formatNumbers';
import {
    EMPTY,
    formatReviews,
    StatTiles,
    Stat,
    SectionHeading,
    KeywordChips,
    GridMap,
    buildMarkers,
} from './visibilityShared';

const COMPETITOR_PIN_COLOR = '#212121';

const ProfileRow = ({ label, value, href }) => (
    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 16 }}>
        <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '6px' }}>
            {label}
        </Typography>
        {href ? (
            <Typography
                variant="body2"
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'var(--primary-main)', wordBreak: 'break-all' }}
            >
                {value}
            </Typography>
        ) : (
            <Typography variant="body2">{value}</Typography>
        )}
    </div>
);

const CompetitorView = ({ view, center, gridMeta = null, onBack }) => {
    const { t } = useTranslation();
    const data = view.data;

    const [keywordId, setKeywordId] = useState(null);
    useEffect(() => {
        setKeywordId(data?.keywords?.[0]?.keywordId ?? null);
    }, [data]);

    const keyword = data?.keywords?.find(item => item.keywordId === keywordId);

    const markers = useMemo(() => {
        const base = buildMarkers(keyword);
        if (data?.place?.latitude != null && data?.place?.longitude != null) {
            base.push({
                lat: data.place.latitude,
                lng: data.place.longitude,
                label: '★',
                color: COMPETITOR_PIN_COLOR,
            });
        }
        return base;
    }, [keyword, data]);

    const categories = data?.place
        ? [data.place.primaryCategory, ...(data.place.categories || [])].filter(Boolean).join(', ')
        : '';

    const profileRows = data?.place
        ? [
              { key: 'address', value: [data.place.address, data.place.city].filter(Boolean).join(', ') },
              { key: 'categories', value: categories },
              { key: 'phone', value: data.place.phone },
              { key: 'website', value: data.place.website, href: data.place.website },
              data.place.googleMapsUrl && {
                  key: 'googleMapsLink',
                  value: t('AnalyticsVisibility.openOnGoogleMaps'),
                  href: data.place.googleMapsUrl,
              },
          ].filter(row => row && row.value)
        : [];

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16 }}>
                <IconButton size="small" aria-label={t('buttons.back')} onClick={onBack}>
                    <ArrowBackIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <Typography variant="subtitle1">{data?.title || ''}</Typography>
            </div>
            {view.loading ? (
                <Skeleton variant="rectangular" width="100%" height={400} />
            ) : view.error || !data ? (
                <Typography variant="body1" align="center" sx={{ display: 'block', padding: '32px 0' }}>
                    {t('AnalyticsVisibility.loadError')}
                </Typography>
            ) : (
                <>
                    <StatTiles>
                        <Stat
                            value={data.topThreeShare != null ? formatPercentNumber(data.topThreeShare * 100) : EMPTY}
                            label={t('AnalyticsVisibility.inTop3')}
                        />
                        <Stat
                            value={data.rating != null ? `${data.rating} ★` : EMPTY}
                            label={t('AnalyticsVisibility.rating')}
                        />
                        <Stat
                            value={formatReviews(data.votes, data.velocityPerMonth)}
                            label={t('AnalyticsVisibility.reviews')}
                        />
                    </StatTiles>
                    {profileRows.length > 0 && (
                        <>
                            <SectionHeading>{t('AnalyticsVisibility.googleProfile')}</SectionHeading>
                            {profileRows.map(row => (
                                <ProfileRow
                                    key={row.key}
                                    label={t(`AnalyticsVisibility.${row.key}`)}
                                    value={row.value}
                                    href={row.href}
                                />
                            ))}
                        </>
                    )}
                    {data.keywords?.length > 0 && (
                        <>
                            <SectionHeading>{t('AnalyticsVisibility.trackedKeywords')}</SectionHeading>
                            <KeywordChips
                                keywords={data.keywords}
                                selectedKeywordId={keywordId}
                                onSelect={setKeywordId}
                            />
                            <GridMap
                                keyword={keyword}
                                mapId="visibility-competitor-map"
                                markers={markers}
                                center={center}
                                gridMeta={gridMeta}
                            />
                            {data.place?.latitude != null && data.place?.longitude != null && (
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{ display: 'block', marginTop: '8px' }}
                                >
                                    {t('AnalyticsVisibility.competitorPinHint')}
                                </Typography>
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default CompetitorView;
